import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Images, noteModels, OrgIntegrations, useModal, User, useSecondaryModal } from 'utils';
import { CustomDelete, Input, ManageActions, MinLoader, NotError, NoteHistoryModal } from 'components';
import { authService as prService } from '../../../../../store/clinical/progressReport/progressReport.service';
import { authService as clientService } from '../../../../../store/client/client.service';
import { clientNoteEnums } from './constants';
import { clientActions, noteActions } from 'store';
import { FormBuilder, formModelEnums } from '../../../../../pages';

const DELETE_ACTION_TYPE = 'DELETE_CLIENT_NOTES';

export const NoteMoreActions = ({ item, requestParams }) => {
    const { userInfo } = User();
    const client = useSelector((state) => state.client.clientItemInfo);
    const [pdfLoading, setPdfLoading] = useState(false);
    const canEditDelete = item?.createdById === userInfo?.id;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { open, close } = useModal();
    const { openSecondary } = useSecondaryModal();

    const triggerBrowserDownload = (url, fileName) => {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName || '');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    /** A progress report downloads its generated PDF; every other form is rendered to a PDF on request. */
    const handleDownloadPdf = async (handleClose) => {
        if (pdfLoading) return;
        dispatch(noteActions.viewNote({ ids: [item.id] }));

        try {
            setPdfLoading(item.id);
            if (item?.type === clientNoteEnums.PROGRESS_REPORT) {
                const res = await prService.getProgressReportDownloadUrlService(item.id);
                const url = res?.data?.url;
                if (!url) throw new Error('Missing download URL');
                triggerBrowserDownload(url, item?.file?.originalName || `${item?.reportTitle || 'progress-report'}.pdf`);
            } else {
                const res = await clientService.downloadClientDocumentService(client?.id, item.id);
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                triggerBrowserDownload(url, `${item?.name || item?.templateTitle || item?.title || 'form'}.pdf`);
                window.URL.revokeObjectURL(url);
            }
            handleClose?.();
        } catch (e) {
            const message = e?.response?.data?.message || e?.data?.message || e?.message || 'Failed to download the PDF';
            toast(<NotError type={'error'} errMessage={message} />);
        } finally {
            setPdfLoading(null);
        }
    };

    const handleDelete = (handleClose) => {
        handleClose();
        const title = item?.type === clientNoteEnums.PROGRESS_REPORT ? 'Progress Report' : 'Delete Note';
        const text =
            item?.type === clientNoteEnums.PROGRESS_REPORT
                ? 'Are you sure you want to delete this Progress Report? This action is not reversible.'
                : 'Are you sure you want to delete this note? This action cannot be undone.';

        open(
            <CustomDelete
                text={text}
                info={title}
                handleDel={() => dispatch(clientActions.deleteClientAllNotes(item?.id, client?.id, requestParams))}
                actionType={DELETE_ACTION_TYPE}
                afterSuccess={close}
                body={
                    <div className="deleting-client-info-box" style={{ alignItems: 'flex-start' }}>
                        <img style={{ marginTop: '3px' }} src={Images.editBlack} alt="icon" />
                        <p style={{ fontWeight: 400 }}>{item?.templateTitle}</p>
                    </div>
                }
            />
        );
    };

    const handleEdit = (handleClose) => {
        handleClose();
        if (item?.type === clientNoteEnums.PROGRESS_REPORT) {
            close();
            const params = {
                reportTitle: item?.title,
                startDate: item?.startDate,
                endDate: item?.endDate,
            };
            navigate(`/progress-report/${item?.templateId}/client/${client?.id}/report/${item?.id}`, { state: params });
        } else {
            if (item?.id) dispatch(noteActions.viewNote({ ids: [item?.id] }));

            open(
                <div>
                    <p className="modal-header-title" style={{ marginBottom: 24 }}>
                        Edit Note
                    </p>
                    <FormBuilder
                        info={item}
                        resourceId={client?.id}
                        formModel={formModelEnums.CLIENTNOTES}
                        ACTION_TYPE={'EDIT_GLOBAL_NOTE'}
                        handleSaveNote={(data) => {
                            const noteData = {
                                ...data,
                                resource: client?.id,
                                onModel: noteModels.Client,
                                name: data?.name,
                            };

                            delete noteData?.id;
                            dispatch(noteActions.editGlobalNote(client?.id, item?.id, noteData, noteModels.Client));
                        }}
                        renderCustomFields={(control, form) => (
                            <div>
                                <Input disabled={!form} name="name" label="Note Name" variant="outlined" control={control} />
                            </div>
                        )}
                    />
                </div>,

                { styles: { bodyStyle: { padding: '24px 48px' } } }
            );
        }
    };

    return (
        <ManageActions
            // actionType={REMIND_NOTE_ACTION_TYPE}
            styleBodyItems={{ padding: 0, width: 200 }}
            btnIcon={Images.moreGrayIcon}
            btnClass={'edit-btn-box'}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            renderBody={(handleClose) => (
                <div>
                    <button
                        disabled={!canEditDelete}
                        style={{ opacity: canEditDelete ? 1 : 0.5 }}
                        className="authorization-action-btn"
                        onClick={(e) => {
                            handleClose();
                            e.stopPropagation();
                            openSecondary(<NoteHistoryModal noteId={item?.id} />);
                        }}>
                        <img src={Images.showEye} alt="edit" />
                        <p>View History</p>
                    </button>

                    {/* Download PDF — every form type */}
                    <button
                        className="authorization-action-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadPdf(handleClose);
                        }}>
                        {pdfLoading === item?.id ? (
                            <MinLoader width={21} margin={'0'} color={'#0C66E4'} position="relative" />
                        ) : (
                            <img src={Images.downloadCloud} alt="download" />
                        )}
                        <p>Download PDF</p>
                    </button>

                    {/* Edit / Review */}
                    {item?.type !== clientNoteEnums.APPT_SESSION_NOTE && (
                        <button
                            disabled={!canEditDelete}
                            style={{ opacity: canEditDelete ? 1 : 0.5 }}
                            className="authorization-action-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(handleClose);
                            }}>
                            <img src={Images.edit} alt="edit" />
                            <p>Edit</p>
                        </button>
                    )}

                    {/* Delete */}
                    {item?.type !== clientNoteEnums?.CLIENT_INTAKE && (
                        <button
                            disabled={!canEditDelete}
                            style={{ opacity: canEditDelete ? 1 : 0.5 }}
                            className="authorization-action-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(handleClose);
                            }}>
                            <img src={Images.redTrash} alt="delete" />
                            <p>Delete</p>
                        </button>
                    )}
                </div>
            )}
        />
    );
};
